<template>
	<article :class="productClasses"
			 class="product"
			 data-product
			 @click="productClicked">

		<div v-if="product.label"
			 :class="`product-label-color-${product.label?.color}`"
			 class="product-price-label">

			{{ product.label.text }}
		</div>

		<div class="product-info">
			<order-block-product-description :product="product"/>
		</div>

		<div v-if="product.priceType !== 'HIDE'" class="product-price">
			<order-block-product-price :product="product"/>
		</div>

		<label v-if="product.orderType === 'RADIO' && !(product.disabled || product.mandatory)" class="product-radio">
			<input :aria-label="product.title"
				   :checked="product.amountOrdered > 0"
				   :name="radioGroup"
				   :value="product.internalKey"
				   type="radio"
				   data-product-ordered
				   @change="handleRadioChange"/>
		</label>
		<div v-else-if="product.orderType !== 'MANDATORY' && !(product.disabled || product.mandatory)"
			 class="toggle-button">
			<order-block-product-toggle-button
				:product="product"
				@updateAmount="updateAmount"
			/>
		</div>
	</article>
</template>

<script lang="ts" setup>
import OrderBlockProductDescription from '@/components/orderblock/OrderBlockProductDescription.vue';
import OrderBlockProductPrice from '@/components/orderblock/OrderBlockProductPrice.vue';
import OrderBlockProductToggleButton from '@/components/orderblock/OrderBlockProductToggleButton.vue';
import {useStore} from '@/store';
import type {Product} from '@/types/product';
import {computed} from 'vue';

const props = defineProps<{
		product: Product
	}>();

const store = useStore();
const productIsRadio = computed(() => props.product.orderType === 'RADIO');
const radioGroup = computed(() => props.product.radioGroup ?? props.product.internalKey.split('_')[0]);

const isProductContractDuration = computed(() => props.product.category === 'CONTRACT_DURATION');

const productClasses = computed(() => {
	const classes = [];

	if (props.product.disabled || props.product.mandatory) {
		return 'disabled';
	}
	if (props.product.image) {
		classes.push('product--image');
	}
	if (productIsRadio.value) {
		classes.push('product--radio');
	}

	if (productIsRadio.value && props.product.amountOrdered === 1) {
		classes.push('product--selected');
	}

	if (isProductContractDuration.value) {
		classes.push('product--contract-duration');
	}

	if (isProductContractDuration.value && !props.product.label) {
		classes.push('product--contract-duration--no-label');
	}

	if (props.product.label) {
		classes.push('product--label');
	}

	return classes.join(' ');
});

const handleRadioChange = (event: Event) => {
	const radio = (event.target as HTMLInputElement);

	if (radio.checked && props.product.amountOrdered === 0) {
		store.toggleOrdered(props.product);
	}
};

const updateAmount = (amount: number) => {
	store.orderAmount(props.product, amount);
};

const productClicked = () => {
	if (productIsRadio.value && !(props.product.disabled || props.product.mandatory)) {
		store.toggleOrdered(props.product);
	}
};
</script>

<style lang="scss" scoped>

.product {
	border: 1px solid #ced4da;
	border-radius: 8px;
	margin: 0 0 8px;

	display: grid;
	gap: 0 16px;
	padding: 16px;

	grid-template-areas: 'info' 'price' 'button';
	grid-template-columns: 1fr;

	&.product--radio {
		position: relative;
		grid-template-areas: 'radio info' 'radio price' 'radio button';
		grid-template-columns: auto 1fr;
		--radio-size: 16px;

		cursor: pointer;

		&:hover {
			border-color: silver;
		}
	}

	&.disabled {
		background: rgba(240, 240, 240, 0.60);

		::v-deep(.product-description) {
			h3, p {
				color: rgba(0, 0, 0, 0.38);
			}
		}

		::v-deep(.product-price) {
			color: rgba(0, 0, 0, 0.38);
		}
	}

	@media screen and (min-width: 520px) {
		gap: 20px;
		grid-template-areas: 'info price' 'info button';
		grid-template-columns: 1fr auto;

		&.product--radio {
			grid-template-areas: 'radio info price';
			grid-template-columns: auto 1fr auto;
			--radio-size: 30px;
		}
	}

	&.product--selected {
		border-color: var(--color-primary_blue);
		background-color: var(--color-primary_lightblue);
	}
}

.product--label {
	padding: 48px 16px 16px;

	.product-price-label {
		display: block;
		position: absolute;
		width: max-content;
		top: 16px;
		left: 50%;
		transform: translate(-50%, -50%);
		padding: 5px 15px;
		border-bottom-left-radius: 10px;
		border-bottom-right-radius: 10px;
		color: var(--color-grey_00);

		&.product-label-color- {
			&blue {
				background: var(--color-primary_blue);
			}

			&orange {
				background: var(--color-secondary-orange);
			}
		}
	}
}

.product--contract-duration {
	text-align: center;

	.product-radio {
		display: none;
	}

	.product-price-label {
		background: var(--color-grey_06);
		color: var(--color-grey_45);
	}

	&.product--selected .product-price-label {
		background: var(--color-primary_blue);
		color: var(--color-grey_00);
	}

	&.product--contract-duration--no-label {
		.product-description {
			margin-top: 32px;

		}
	}
}

.product-info {
	grid-area: info;
}

.product-price {
	grid-area: price;
}

.product-radio {
	grid-area: radio;
	display: grid;
	align-content: center;
	justify-content: center;
	align-self: stretch;
	justify-self: stretch;

	width: auto;

	margin: -16px -16px -16px -16px;
	padding: 16px;
	cursor: inherit;

	input {
		accent-color: var(--color-primary_blue);
		font-size: var(--radio-size, 1em);
		width: 1em;
		height: 1em;
		margin: 0;
		cursor: inherit;
	}
}

.toggle-button {
	grid-area: button;
	align-self: end;
}
</style>
